import { get_token } from "@/router";

const live_camera_base_url = process.env.VUE_APP_LIVE_CAMERA_BASE_URL;
const camera_id_pattern = /^[a-z0-9-]+$/i;
const camera_id_header = "X-Camera-Id";

function get_trusted_camera_base_url() {
  if (!live_camera_base_url) {
    return null;
  }

  let base_url;
  try {
    base_url = new URL(live_camera_base_url, window.location.origin);
  } catch (_error) {
    return null;
  }

  return ["http:", "https:"].includes(base_url.protocol) ? base_url : null;
}

function build_camera_endpoint_urls() {
  const trusted_base_url = get_trusted_camera_base_url();
  if (!trusted_base_url) {
    return null;
  }

  const root = trusted_base_url.href.replace(/\/+$/, "");

  // These three endpoints are fixed strings computed once from trusted, env-configured
  // config only. No per-request or component data (camera id, feed props, etc.) ever
  // takes part in building a fetch target -- the camera id always travels in the
  // X-Camera-Id request header instead, so the URL passed to fetch() never varies with
  // user/component-controlled input.
  return {
    start: `${root}/start/`,
    status: `${root}/status/`,
    stop: `${root}/stop/`
  };
}

const camera_endpoint_urls = build_camera_endpoint_urls();

function get_camera_id(feed) {
  const camera_id = String(feed?.cameraId || "").trim();
  return camera_id_pattern.test(camera_id) ? camera_id : "";
}

export default {
  data() {
    return ({
      image_failed: false,
      refresh_key: Date.now(),
      is_loading: false,
      status_message: "",
      warmup_timer: null,
      reconnect_timer: null
    });
  },
  methods: {
    auth_headers() {
      const token = get_token();
      return token ? { Authorization: `Token ${token}` } : {};
    },
    is_hls_feed(feed = this.feed) {
      const url = feed?.url || "";
      return String(feed?.type || "").toLowerCase() === "hls"
        || /\.m3u8(\?|#|$)/i.test(url);
    },
    async prepare_feed() {
      if (!this.stream_url || this.render_mode !== "video") {
        this.destroy_hls_player();
        return;
      }

      if (this.is_hls_feed()) {
        if (this.attached_stream_url === this.stream_url && this.hls) {
          return;
        }

        await this.prepare_hls_feed();
        return;
      }

      this.destroy_hls_player();
      this.attach_native_video(this.live_feed_src);
    },
    async prepare_hls_feed() {
      this.is_loading = true;
      this.image_failed = false;
      this.refresh_key = Date.now();
      this.status_message = "Connecting to camera stream...";
      this.hls_error_count = 0;

      try {
        if (!get_token()) {
          throw new Error("Sign in again before opening the live feed.");
        }

        await this.start_hls_proxy();
        await this.wait_for_hls_playlist();
      } catch (error) {
        this.status_message = error.message || "Unable to load live footage.";
        this.image_failed = true;
        this.is_loading = false;
      }
    },
    async start_hls_proxy() {
      if (!get_camera_id(this.feed)) {
        return;
      }

      await this.fetch_camera_endpoint("start", {
        method: "POST",
        retryMessage: "Camera stream is starting..."
      });
    },
    stop_hls_proxy(feed = this.feed) {
      if (!this.is_hls_feed(feed)) {
        return;
      }

      const camera_id = get_camera_id(feed);
      if (!camera_id || !camera_endpoint_urls) {
        return;
      }

      fetch(camera_endpoint_urls.stop, {
        method: "POST",
        headers: {
          ...this.auth_headers(),
          [camera_id_header]: camera_id
        },
        keepalive: true
      }).catch(() => undefined);
    },
    async wait_for_hls_playlist(attempt = 0) {
      if (!get_camera_id(this.feed)) {
        await this.attach_hls_player();
        return;
      }

      const payload = await this.fetch_camera_endpoint("status", {
        method: "GET",
        retryMessage: "Waiting for camera stream..."
      });

      if (payload.playlist_ready) {
        await this.attach_hls_player();
        return;
      }

      if (payload.ffmpeg_available && payload.running === false && attempt < 5) {
        await this.start_hls_proxy();
      }

      if (!payload.ffmpeg_available) {
        throw new Error(payload.message || "ffmpeg is not installed on the backend server.");
      }

      if (attempt > 30) {
        throw new Error("The camera stream did not become ready in time.");
      }

      this.status_message = payload.message || "Waiting for live video...";
      this.warmup_timer = window.setTimeout(() => {
        this.wait_for_hls_playlist(attempt + 1).catch((error) => {
          this.status_message = error.message || "Unable to load live footage.";
          this.image_failed = true;
          this.is_loading = false;
        });
      }, 1000);
    },
    async fetch_camera_endpoint(endpoint, options = {}, attempt = 0) {
      const camera_id = get_camera_id(this.feed);
      let endpoint_url;
      switch (endpoint) {
        case "start":
          endpoint_url = camera_endpoint_urls?.start;
          break;
        case "status":
          endpoint_url = camera_endpoint_urls?.status;
          break;
        default:
          endpoint_url = undefined;
      }

      if (!camera_id || !endpoint_url) {
        throw new Error("Camera stream URL is not configured.");
      }

      const response = await fetch(endpoint_url, {
        method: options.method || "GET",
        headers: {
          Accept: "application/json",
          [camera_id_header]: camera_id,
          ...this.auth_headers()
        }
      });
      const payload = await response.json().catch(() => ({}));

      if (response.ok) {
        return payload;
      }

      if (response.status === 503 && attempt < 5) {
        this.status_message = options.retryMessage || "Reconnecting camera...";
        await this.delay(1000 + attempt * 500);
        return this.fetch_camera_endpoint(endpoint, options, attempt + 1);
      }

      throw new Error(
        payload.message
        || payload.last_error
        || "Unable to connect to camera stream."
      );
    },
    delay(milliseconds) {
      return new Promise(resolve => {
        window.setTimeout(resolve, milliseconds);
      });
    },
    reconnect_hls_after_delay() {
      this.clear_reconnect_timer();
      this.reconnect_timer = window.setTimeout(() => {
        this.destroy_hls_player();
        this.prepare_hls_feed();
      }, 2000);
    },
    clear_warmup_timer() {
      if (this.warmup_timer) {
        window.clearTimeout(this.warmup_timer);
        this.warmup_timer = null;
      }
    },
    clear_reconnect_timer() {
      if (this.reconnect_timer) {
        window.clearTimeout(this.reconnect_timer);
        this.reconnect_timer = null;
      }
    },
    refresh_feed() {
      this.image_failed = false;
      if (!this.is_hls_feed()) {
        this.refresh_key = Date.now();
      }
      this.clear_warmup_timer();
      this.clear_reconnect_timer();
      this.destroy_hls_player();
      this.$nextTick(() => {
        this.prepare_feed();
      });
    },
    mark_feed_failed() {
      if (this.is_hls_feed() && this.hls_error_count <= 5) {
        this.status_message = "Reconnecting live stream...";
        this.is_loading = true;
        this.reconnect_hls_after_delay();
        return;
      }

      this.image_failed = true;
      this.is_loading = false;
    },
    mark_feed_loaded() {
      this.image_failed = false;
      this.is_loading = false;
      this.status_message = "";
    }
  }
};
